import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  helpSections,
  helpTitlePage,
  helpSectionElementId,
  helpItemId,
  flattenHelpSections,
} from "./helpContent";
import { buildHelpIndex, searchHelp } from "./helpSearch";

// Full User Manual PDF (from Xlog3D_User_Manual_Edition.1_Oct26.docx). Bundled
// via webpack file-loader so the URL resolves under both the web build and the
// Electron file:// load; `.default` holds the URL (react-scripts 4 convention).
// Replace src/docs/Xlog3D_Handbook.pdf and rebuild to ship an updated edition.
const handbookPdf = require("../../docs/Xlog3D_Handbook.pdf").default;

const brand = "#c0392b";
const text = "#333";

const tabBase = {
  border: "none",
  background: "transparent",
  padding: "8px 14px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  color: "#666",
  borderBottom: "2px solid transparent",
  marginBottom: "-1px",
};

const isFieldItem = (item) =>
  item && typeof item === "object" && "name" in item && "desc" in item;

// Convert a Title Case title to sentence case while preserving known
// proper nouns and acronyms used in this app.
const toSentenceCase = (s) => {
  if (!s) return s;
  let out = s.toLowerCase();
  out = out.charAt(0).toUpperCase() + out.slice(1);
  out = out.replace(/xlog3d/gi, "Xlog3D");
  out = out.replace(/\b3d\b/g, "3D");
  out = out.replace(/\bspl\b/g, "SPL");
  out = out.replace(/\brd\b/g, "RD");
  out = out.replace(/\bp6\b/g, "P6");
  out = out.replace(/\bd-shaped\b/g, "D-shaped");
  out = out.replace(/\bd-shape\b/g, "D-shape");
  out = out.replace(/\bdpr\b/g, "DPR");
  out = out.replace(/\bhrt\b/g, "HRT");
  out = out.replace(/\bdips\b/g, "DIPS");
  out = out.replace(/\bhimalaya\b/g, "Himalaya");
  out = out.replace(/\basf\b/g, "Asf");
  out = out.replace(/\bchmin\b/g, "ChMin");
  out = out.replace(/\bchmax\b/g, "ChMax");
  return out;
};

const HelpButton = () => {
  const [open, setOpen] = useState(false);
  const [panelTab, setPanelTab] = useState("guide");
  const [askQuery, setAskQuery] = useState("");
  const [askResults, setAskResults] = useState([]);
  const [activeId, setActiveId] = useState(helpSections[0]?.id || null);

  const flatSections = useMemo(() => flattenHelpSections(helpSections), []);
  const helpIndex = useMemo(() => buildHelpIndex(helpSections), []);
  const contentRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("xlog3d:open-help", onOpen);
    window.openXlog3DHelp = onOpen;
    return () => {
      window.removeEventListener("xlog3d:open-help", onOpen);
      if (window.openXlog3DHelp === onOpen) delete window.openXlog3DHelp;
    };
  }, []);

  const runSearch = useCallback(() => {
    setAskResults(searchHelp(helpIndex, askQuery, 8));
  }, [helpIndex, askQuery]);

  // Scroll-spy: highlight the deepest section whose heading has scrolled past
  // the top of the viewport. flatSections is already in document order, so
  // iterating and picking the last passed-the-top entry naturally selects a
  // subsection over its parent when both wrap the visible area.
  useEffect(() => {
    if (!open || panelTab !== "guide") return;
    const root = contentRef.current;
    if (!root) return;

    const compute = () => {
      const rootTop = root.getBoundingClientRect().top + 16;
      let candidate = flatSections[0]?.id;
      for (const s of flatSections) {
        const el = document.getElementById(helpSectionElementId(s.id));
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - rootTop <= 0) candidate = s.id;
        else break;
      }
      if (candidate) {
        setActiveId((prev) => (prev === candidate ? prev : candidate));
      }
    };

    compute();
    root.addEventListener("scroll", compute, { passive: true });
    return () => root.removeEventListener("scroll", compute);
  }, [open, panelTab, flatSections]);

  const scrollToSection = (sectionId) => {
    setPanelTab("guide");
    setActiveId(sectionId);
    setTimeout(() => {
      const el = document.getElementById(helpSectionElementId(sectionId));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const scrollToItem = (id, sectionId) => {
    setOpen(true);
    setPanelTab("guide");
    if (sectionId) setActiveId(sectionId);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.outline = `2px solid ${brand}`;
        setTimeout(() => {
          el.style.outline = "none";
        }, 1600);
      }
    }, 50);
  };

  const onAskKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      runSearch();
    }
  };

  const renderItems = (items, sectionId) => {
    if (!items || items.length === 0) return null;
    return (
      <ul style={{ margin: "0 0 0 4px", padding: 0, listStyle: "none" }}>
        {items.map((item, idx) => (
          <li
            id={helpItemId(sectionId, idx)}
            key={idx}
            style={{
              fontSize: "13px",
              lineHeight: 1.55,
              color: text,
              marginBottom: "8px",
              paddingLeft: "12px",
              borderLeft: "2px solid #eee",
              scrollMarginTop: "8px",
            }}
          >
            {isFieldItem(item) ? (
              <>
                <span style={{ fontWeight: 600 }}>{item.name}: </span>
                <span style={{ whiteSpace: "pre-line" }}>{item.desc}</span>
              </>
            ) : (
              item
            )}
          </li>
        ))}
      </ul>
    );
  };

  const renderSection = (section) => {
    const headingSize =
      section.depth === 0 ? "16px" : section.depth === 1 ? "14px" : "13px";
    const isThirdLevel = section.depth >= 2;
    return (
      <section
        key={section.id}
        id={helpSectionElementId(section.id)}
        style={{
          marginBottom: section.depth === 0 ? "20px" : "14px",
          marginLeft: section.depth > 1 ? `${(section.depth - 1) * 8}px` : 0,
          scrollMarginTop: "8px",
        }}
      >
        <h2
          style={{
            margin: "0 0 8px 0",
            fontSize: headingSize,
            fontWeight: 700,
            color: section.depth === 0 ? brand : "#000",
            textTransform: "none",
            letterSpacing: 0,
            paddingLeft: isThirdLevel ? "16px" : 0,
            position: "relative",
          }}
        >
          {isThirdLevel && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 0,
                color: "#000",
              }}
            >
              •
            </span>
          )}
          {toSentenceCase(section.title)}
        </h2>
        {section.intro && (
          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "13px",
              lineHeight: 1.55,
              color: "#555",
            }}
          >
            {section.intro}
          </p>
        )}
        {renderItems(section.items, section.id)}
        {section.image && (
          <figure style={{ margin: "12px 0 4px", textAlign: "center" }}>
            <img
              src={section.image.src}
              alt={section.image.alt || ""}
              style={{
                maxWidth: "100%",
                height: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}
            />
            {section.image.alt && (
              <figcaption
                style={{
                  fontSize: "11px",
                  color: "#888",
                  marginTop: "6px",
                  lineHeight: 1.4,
                }}
              >
                {section.image.alt}
              </figcaption>
            )}
          </figure>
        )}
        {section.subsections &&
          section.subsections.map((sub) =>
            renderSection({ ...sub, depth: (section.depth || 0) + 1 })
          )}
        {section.caution && (
          <p
            style={{
              margin: "10px 0 0",
              padding: "8px 12px",
              fontSize: "13px",
              lineHeight: 1.55,
              color: "#7a5c00",
              background: "#fff8e1",
              border: "1px solid #f0e0a0",
              borderRadius: "6px",
            }}
          >
            <strong>Caution:</strong> {section.caution}
          </p>
        )}
      </section>
    );
  };

  const contentsSidebar = (
    <nav
      aria-label="Table of contents"
      style={{
        flex: "0 0 220px",
        borderRight: "1px solid #eee",
        padding: "12px 8px 12px 12px",
        overflowY: "auto",
        background: "#fafafa",
        fontSize: "12px",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: brand,
          marginBottom: "8px",
          textTransform: "uppercase",
          letterSpacing: "0.4px",
        }}
      >
        Contents
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {flatSections.map((s) => {
          const isActive = activeId === s.id;
          const tocColor =
            isActive ? brand : s.depth === 0 ? brand : s.depth === 1 ? "#000" : text;
          const tocWeight =
            isActive ? 700 : s.depth <= 1 ? 700 : 400;
          return (
            <li key={s.id} style={{ margin: "1px 0" }}>
              <button
                type="button"
                onClick={() => scrollToSection(s.id)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: isActive ? "#fff" : "none",
                  border: "none",
                  color: tocColor,
                  fontWeight: tocWeight,
                  textTransform: "none",
                  cursor: "pointer",
                  padding: `5px 8px 5px ${8 + s.depth * 12}px`,
                  fontSize: s.depth === 0 ? "12.5px" : "12px",
                  lineHeight: 1.35,
                  borderRadius: "6px",
                  borderLeft: isActive
                    ? `3px solid ${brand}`
                    : "3px solid transparent",
                  transition: "background 0.15s, color 0.15s",
                  listStyleType: s.depth >= 2 ? "disc" : "none",
                  listStylePosition: "outside",
                }}
              >
                {s.depth >= 2
                  ? `• ${toSentenceCase(s.title)}`
                  : toSentenceCase(s.title)}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );

  return (
    <>
      {open && (
        <div
          role="presentation"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
          }}
        />
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-panel-title"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(94vw, 860px)",
            maxHeight: "88vh",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: brand,
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <span id="help-panel-title" style={{ fontSize: "18px", fontWeight: 600 }}>
              User Manual
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <a
                href={handbookPdf}
                download="Xlog3D_User_Manual.pdf"
                target="_blank"
                rel="noopener noreferrer"
                title="Download the full User Manual as a PDF"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  border: "1px solid rgba(255,255,255,0.75)",
                  borderRadius: "6px",
                  padding: "5px 10px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="fa fa-download" aria-hidden="true"></i>
                Download PDF
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: "22px",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: "0 4px",
                }}
                aria-label="Close help"
              >
                &times;
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #e0e0e0",
              padding: "0 12px",
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={() => setPanelTab("guide")}
              style={{
                ...tabBase,
                color: panelTab === "guide" ? brand : "#666",
                borderBottom:
                  panelTab === "guide" ? `2px solid ${brand}` : "2px solid transparent",
              }}
            >
              Guide &amp; contents
            </button>
            <button
              type="button"
              onClick={() => setPanelTab("ask")}
              style={{
                ...tabBase,
                color: panelTab === "ask" ? brand : "#666",
                borderBottom:
                  panelTab === "ask" ? `2px solid ${brand}` : "2px solid transparent",
              }}
            >
              Search help
            </button>
          </div>

          {panelTab === "guide" && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {contentsSidebar}
              <div
                ref={contentRef}
                style={{
                  flex: 1,
                  padding: "16px 18px 24px",
                  overflowY: "auto",
                  minWidth: 0,
                }}
              >
                <header style={{ textAlign: "center", marginBottom: "20px" }}>
                  <h1 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700, color: brand }}>
                    {helpTitlePage.title}
                  </h1>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>
                    {helpTitlePage.subtitle}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                    {helpTitlePage.edition}
                  </div>
                  <p style={{ margin: "10px 0 0", fontSize: "13px", fontStyle: "italic", color: "#555" }}>
                    {helpTitlePage.welcome}
                  </p>
                </header>
                {helpSections.map((section) =>
                  renderSection({ ...section, depth: 0 })
                )}
              </div>
            </div>
          )}

          {panelTab === "ask" && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {contentsSidebar}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  padding: "14px 16px 16px",
                  overflow: "hidden",
                }}
              >
              <p style={{ fontSize: "12px", color: "#666", margin: "0 0 10px 0" }}>
                Searches this help in your browser only (no server, no AI API cost). It
                ranks keyword and short-phrase matches.
              </p>
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <textarea
                  value={askQuery}
                  onChange={(e) => setAskQuery(e.target.value)}
                  onKeyDown={onAskKey}
                  placeholder="e.g. chainage, SPL, compass, crown…"
                  rows={2}
                  style={{
                    flex: 1,
                    resize: "vertical",
                    minHeight: "44px",
                    padding: "8px 10px",
                    fontSize: "13px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    fontFamily: "inherit",
                  }}
                  aria-label="Search help"
                />
                <button
                  type="button"
                  onClick={runSearch}
                  style={{
                    alignSelf: "flex-start",
                    backgroundColor: brand,
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Search
                </button>
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  minHeight: 0,
                }}
              >
                {askResults.length === 0 && askQuery.trim() && (
                  <p style={{ fontSize: "12px", color: "#888" }}>
                    No matches. Try other words or open the guide.
                  </p>
                )}
                {askResults.length === 0 && !askQuery.trim() && (
                  <p style={{ fontSize: "12px", color: "#888" }}>
                    Type a question or keywords, then press Search or Enter.
                  </p>
                )}
                {askResults.map((r) => {
                  const item = r.item;
                  const snip =
                    item.text.length > 220 ? item.text.slice(0, 217) + "…" : item.text;
                  return (
                    <div
                      key={item.id}
                      style={{
                        border: "1px solid #eee",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        marginBottom: "8px",
                        background: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: brand,
                          fontWeight: 700,
                          marginBottom: "4px",
                        }}
                      >
                        {toSentenceCase(item.sectionTitle)}
                      </div>
                      <p
                        style={{
                          fontSize: "12px",
                          color: text,
                          margin: "0 0 8px 0",
                          lineHeight: 1.5,
                        }}
                      >
                        {snip}
                      </p>
                      <button
                        type="button"
                        onClick={() => scrollToItem(item.id, item.sectionId)}
                        style={{
                          background: "none",
                          border: "none",
                          color: brand,
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          padding: 0,
                          textDecoration: "underline",
                        }}
                      >
                        Open in guide
                      </button>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default HelpButton;
